import '@adornis/buildify/client/components/x-buildify-file-selection.js';
import { ExtensionBoilerplate } from '@adornis/buildify/client/extensions/ContentExtensionBoilerplate.js';
import { EditExtensionData } from '@adornis/buildify/client/extensions/EditExtensionData.js';
import type { XExtensionEditor } from '@adornis/buildify/client/extensions/x-extension-editor.js';
import type { BuildifyExtension } from '@adornis/buildify/client/globals/types.js';
import type { RenderableConfigFormController } from '@adornis/buildify/client/RenderableConfigFormController.js';
import type { Base } from '@adornis/buildify/db/Base.js';
import { BaseH3 } from '@adornis/buildify/db/extensions/text/BaseH3.js';
import { BaseText } from '@adornis/buildify/db/extensions/text/BaseText.js';
import type { BuildifyGlobalSettings } from '@adornis/buildify/db/GlobalSettings.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { ComponentSizeController } from '@adornis/chemistry/controllers/component-size-controller.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/stack/x-stack.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import '@adornis/digitale-helden-shared/client/components/component-carousel.js';
import { ComponentCarousel } from '@adornis/digitale-helden-shared/client/components/component-carousel.js';
import '@adornis/digitale-helden-shared/client/components/component-icon-button.js';
import '@adornis/digitale-helden-shared/client/theme/buildify/extensions/image/BuildifyCampusImageExtension';
import { LASPromptSwapIndexList } from '@adornis/digitale-helden-shared/client/theme/buildify/extensions/list/las-prompt-swap-index-list.js';
import { DubniumBaseTag } from '@adornis/digitale-helden-shared/db/buildify/DubniumBaseTag.js';
import { Link } from '@adornis/digitale-helden-shared/db/buildify/Link.js';
import { mobile } from '@adornis/digitale-helden-shared/db/enums.js';
import '@adornis/dragdrop/x-drag-drop.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { createRef, ref } from 'lit/directives/ref.js';
import { repeat } from 'lit/directives/repeat.js';
import { BaseCarousel, BaseCarouselItemExpandingText, BaseCarouselItemTextUnderImage } from '../../db/BaseCarousel.js';
import './marketing-carousel-item.js';

export const MarketingCarouselExtension = ({
  textMaxCharExtension,
  h3Extension,
  smallTextExtension,
  tagExtension,
}: {
  textMaxCharExtension: BuildifyExtension<BaseText>;
  tagExtension: BuildifyExtension<DubniumBaseTag>;
  smallTextExtension: BuildifyExtension<BaseText>;
  h3Extension: BuildifyExtension<BaseH3>;
}): BuildifyExtension<BaseCarousel> => ({
  class: BaseCarousel,
  toolbar: {
    icon: 'rectangle-vertical-history',
    text: {
      default: 'Karussell',
    },
  },
  group: 'Marketing',
  render(instance, { isSubelement, dropzoneBefore, dropzoneAfter }) {
    return html`
      <marketing-carousel
        .tagExtension=${tagExtension}
        .textMaxCharExtension=${textMaxCharExtension}
        .h3Extension=${h3Extension}
        .smallTextExtension=${smallTextExtension}
        .content=${instance}
        .defaultValues=${this.defaultValues}
        ?sub=${isSubelement}
        .hasDropzoneBefore=${dropzoneBefore}
        .hasDropzoneAfter=${dropzoneAfter}
      ></marketing-carousel>
    `;
  },
  editor: ({ content, contentController, controllerBaseKeyPath, host, extensions, globalSettings }) => {
    return html`
      <marketing-carousel-editor
        .tagExtension=${tagExtension}
        .textMaxCharExtension=${textMaxCharExtension}
        .h3Extension=${h3Extension}
        .smallTextExtension=${smallTextExtension}
        .content=${content}
        .contentController=${contentController}
        .controllerBaseKeyPath=${controllerBaseKeyPath}
        .host=${host}
        .extensions=${extensions}
        .globalSettings=${globalSettings}
      ></marketing-carousel-editor>
    `;
  },
});

@customElement('marketing-carousel')
export class MCarousel extends ExtensionBoilerplate<BaseCarousel> {
  @property({ attribute: false }) textMaxCharExtension?: BuildifyExtension;
  @property({ attribute: false }) tagExtension?: BuildifyExtension;
  @property({ attribute: false }) smallTextExtension?: BuildifyExtension;
  @property({ attribute: false }) h3Extension?: BuildifyExtension;

  @state() private readonly _componentSizeController = new ComponentSizeController(this);
  @state() private readonly _carouselElement = createRef<ComponentCarousel>();

  override _content(content: BaseCarousel) {
    const isMobile = (this._componentSize.value?.width ?? 0) <= mobile;
    const carouselWidth = this._carouselElement.value?.clientWidth ?? 0;
    const minCardWidth = 350;
    const countItems = Math.floor(carouselWidth / minCardWidth);

    return html`
      <component-carousel
        ${ref(this._carouselElement)}
        item-gap="24"
        min-height=${ifDefined(isMobile ? undefined : '580')}
        overflow="visible"
        duration="350"
        visible-items=${countItems > 0 ? countItems : 1}
        ?infinity=${content.isInfinityScroll}
        .items=${(content.items ?? []).map(
          item => (isActive: boolean) =>
            html`
              <marketing-carousel-item
                .textMaxCharExtension=${this.textMaxCharExtension}
                .smallTextExtension=${this.smallTextExtension}
                .tagExtension=${this.tagExtension}
                .h3Extension=${this.h3Extension}
                .item=${item}
                .isActive=${isActive}
              ></marketing-carousel-item>
            `,
        )}
      >
      </component-carousel>
    `;
  }
}

@customElement('marketing-carousel-editor')
export class MCarouselEditor extends ChemistryLitElement {
  @property({ attribute: false }) content!: BaseCarousel;
  @property({ attribute: false }) contentController!: RenderableConfigFormController<Base>;
  @property({ attribute: false }) controllerBaseKeyPath!: Array<string | number>;
  @property({ attribute: false }) host!: XExtensionEditor<Base>;
  @property({ attribute: false }) textMaxCharExtension?: BuildifyExtension;
  @property({ attribute: false }) tagExtension!: BuildifyExtension;
  @property({ attribute: false }) smallTextExtension!: BuildifyExtension;
  @property({ attribute: false }) h3Extension!: BuildifyExtension;
  @property({ attribute: false }) extensions!: BuildifyExtension<any>[];
  @property({ attribute: false }) globalSettings!: BuildifyGlobalSettings;

  override render() {
    return html`
      <d-flex space="lg">
        <d-checkbox
          ${this.contentController.field(...this.controllerBaseKeyPath, 'isInfinityScroll')}
          .label=${'Unendlicher-Scroll'}
        ></d-checkbox>
        <d-flex space="sm">
          ${(this.content.items?.length ?? 0) >= 2
            ? html`
                <d-button
                  @click=${async () => {
                    const content = this.content;
                    if (!content) return;
                    const items = content.items ?? [];
                    const resorted = await LASPromptSwapIndexList.swapIndexes(items, item => {
                      const index = items.findIndex(i => i._id === item._id);
                      return `${index + 1}. Karussell-Item (${
                        item instanceof BaseCarouselItemExpandingText ? 'Produkt' : 'Kategorie'
                      })`;
                    });

                    if (resorted) content.items = resorted as any[];
                    this.host.requestUpdate();
                  }}
                >
                  Reihenfolge verändern
                </d-button>
              `
            : nothing}

          <x-flex ${css({ flex: 1 })} space="md">
            ${repeat(
              this.content.items ?? [],
              item => item._id,
              (item, index) => {
                if (
                  !(item instanceof BaseCarouselItemExpandingText) &&
                  !(item instanceof BaseCarouselItemTextUnderImage)
                )
                  return nothing;

                return html`
                  <d-flex flex item space="sm" crossaxis-center horizontal w100 key=${index}>
                    <stack-item
                      flex
                      name=${`${index + 1}. Karussell-Item (${
                        item instanceof BaseCarouselItemExpandingText ? 'Produkt' : 'Kategorie'
                      })`}
                      .renderContent=${() => html`
                        <d-flex space="md">
                          <!-- tag -->
                          ${this.renderTag(item, index)}

                          <!-- tag on hover -->
                          ${this.renderTagOnHover(item, index)}

                          <!-- image -->
                          ${this.renderImage(item, index)}

                          <!-- link -->
                          ${this.renderLink(item, index)}

                          <!-- texts -->
                          <d-text> Texte </d-text>
                          <!-- heading -->
                          ${this.renderHeading(item, index)}
                          <!-- small text -->
                          ${item instanceof BaseCarouselItemExpandingText ? this.renderSmallText(item, index) : nothing}
                          <!-- text -->
                          ${this.renderText(item, index)}
                          <!-- text on hover -->
                          ${item instanceof BaseCarouselItemExpandingText
                            ? this.renderTextOnHover(item, index)
                            : nothing}
                        </d-flex>
                      `}
                    >
                    </stack-item>
                    <d-icon-button
                      icon="trash"
                      background=${this.host.colors.tone.warning}
                      @click=${() => {
                        if (!this.content.items) this.content.items = [];
                        this.content.items.splice(index, 1);
                        this.host.requestUpdate();
                      }}
                    ></d-icon-button>
                  </d-flex>
                `;
              },
            )}
          </x-flex>
        </d-flex>

        <!-- buttons start -->
        <d-flex space="sm">
          <d-icon-button
            ${css({ alignSelf: 'end' })}
            text="Produkt"
            icon="plus"
            @click=${() => {
              if (!this.content.items) this.content.items = [];

              this.content.items.push(
                new BaseCarouselItemExpandingText({
                  tag: new DubniumBaseTag({}),
                  tagOnHover: new DubniumBaseTag({}),
                  heading: new BaseH3({}),
                  smallText: new BaseText({}),
                  text: new BaseText({}),
                  textOnHover: new BaseText({}),
                  link: new Link({}),
                }),
              );
              this.host.requestUpdate();
            }}
          ></d-icon-button>
          <d-icon-button
            ${css({ alignSelf: 'end' })}
            text="Kategorie"
            icon="plus"
            @click=${() => {
              if (!this.content.items) this.content.items = [];

              this.content.items.push(
                new BaseCarouselItemTextUnderImage({
                  tag: new DubniumBaseTag({}),
                  tagOnHover: new DubniumBaseTag({}),
                  heading: new BaseH3({}),
                  text: new BaseText({}),
                  link: new Link({}),
                }),
              );
              this.host.requestUpdate();
            }}
          ></d-icon-button>
        </d-flex>
        <!-- buttons end -->
      </d-flex>
    `;
  }

  protected renderTag(item: BaseCarouselItemExpandingText | BaseCarouselItemTextUnderImage, index: number) {
    return html`
      <stack-item
        flex
        name="Tags"
        .renderContent=${() => html`
          ${EditExtensionData({
            host: this.host,
            content: {
              value: item.tag,
              path: [...this.controllerBaseKeyPath, 'items', index, 'tag'],
              controller: this.contentController,
              extension: this.tagExtension,
            },
            globalSettings: this.globalSettings,
            extensions: this.extensions,
          })}
        `}
      ></stack-item>
    `;
  }

  protected renderTagOnHover(item: BaseCarouselItemExpandingText | BaseCarouselItemTextUnderImage, index: number) {
    return html`
      <stack-item
        flex
        name="Tags (Hover)"
        .renderContent=${() => html`
          ${EditExtensionData({
            host: this.host,
            content: {
              value: item.tagOnHover,
              path: [...this.controllerBaseKeyPath, 'items', index, 'tagOnHover'],
              controller: this.contentController,
              extension: this.tagExtension,
            },
            globalSettings: this.globalSettings,
            extensions: this.extensions,
          })}
        `}
      >
      </stack-item>
    `;
  }

  protected renderImage(item: BaseCarouselItemExpandingText | BaseCarouselItemTextUnderImage, index: number) {
    return html`
      <stack-item
        flex
        name="Bild"
        .renderContent=${() => html`
          <campus-buildify-image-selector
            ${this.contentController.field(...this.controllerBaseKeyPath, 'items', index, 'imageID')}
          ></campus-buildify-image-selector>
        `}
      >
      </stack-item>
    `;
  }

  protected renderLink(item: BaseCarouselItemExpandingText | BaseCarouselItemTextUnderImage, index: number) {
    return html`
      <stack-item
        flex
        name="Link"
        .renderContent=${() => html`
          <d-link-editor
            ${this.contentController.field(...this.controllerBaseKeyPath, 'items', index, 'link')}
          ></d-link-editor>
        `}
      >
      </stack-item>
    `;
  }

  protected renderHeading(item: BaseCarouselItemExpandingText | BaseCarouselItemTextUnderImage, index: number) {
    return html`
      <stack-item
        flex
        name="Überschrift"
        .renderContent=${() => html`
          ${EditExtensionData({
            host: this.host,
            content: {
              value: item.heading,
              path: [...this.controllerBaseKeyPath, 'items', index, 'heading'],
              controller: this.contentController,
              extension: this.h3Extension,
            },
            globalSettings: this.globalSettings,
            extensions: this.extensions,
          })}
        `}
      >
      </stack-item>
    `;
  }

  protected renderSmallText(item: BaseCarouselItemExpandingText, index: number) {
    return html`
      <stack-item
        flex
        name="Kleiner Text"
        .renderContent=${() => html`
          ${EditExtensionData({
            host: this.host,
            content: {
              value: item.smallText,
              path: [...this.controllerBaseKeyPath, 'items', index, 'smallText'],
              controller: this.contentController,
              extension: this.smallTextExtension,
            },
            globalSettings: this.globalSettings,
            extensions: this.extensions,
          })}
        `}
      >
      </stack-item>
    `;
  }

  protected renderText(item: BaseCarouselItemExpandingText | BaseCarouselItemTextUnderImage, index: number) {
    return html`
      <stack-item
        flex
        name="Text"
        .renderContent=${() => html`
          ${EditExtensionData({
            host: this.host,
            content: {
              value: item.text,
              path: [...this.controllerBaseKeyPath, 'items', index, 'text'],
              controller: this.contentController,
              extension: this.textMaxCharExtension,
            },
            globalSettings: this.globalSettings,
            extensions: this.extensions,
          })}
        `}
      >
      </stack-item>
    `;
  }

  protected renderTextOnHover(item: BaseCarouselItemExpandingText, index: number) {
    return html`
      <stack-item
        flex
        name="Text (hover)"
        .renderContent=${() => html`
          ${EditExtensionData({
            host: this.host,
            content: {
              value: item.textOnHover,
              path: [...this.controllerBaseKeyPath, 'items', index, 'textOnHover'],
              controller: this.contentController,
              extension: this.textMaxCharExtension,
            },
            globalSettings: this.globalSettings,
            extensions: this.extensions,
          })}
        `}
      >
      </stack-item>
    `;
  }
}
